import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addSupplierResponse,
  createSupplier,
  deleteSupplier,
  fetchAdminSuppliers,
  fetchSupplierPacket,
  fetchSupplierSuggestion,
  sendSupplierEmail,
  translateSupplierBody,
  updateSupplier,
  type AdminSupplierPayload,
  type SupplierResponsePayload,
  type SupplierSendPayload,
} from "@/api/suppliers";

const SUPPLIERS_KEY = ["adminCenter", "suppliers"] as const;

/** D-133 A-W3 — admin supplier directory (admin/QueueAdmin; skip when not allowed). */
export function useAdminSuppliers(enabled = true) {
  return useQuery({
    queryKey: SUPPLIERS_KEY,
    queryFn: ({ signal }) => fetchAdminSuppliers(signal),
    staleTime: 15_000,
    enabled,
  });
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminSupplierPayload) => createSupplier(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: SUPPLIERS_KEY }),
  });
}

export function useUpdateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AdminSupplierPayload }) => updateSupplier(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: SUPPLIERS_KEY }),
  });
}

export function useDeleteSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSupplier(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: SUPPLIERS_KEY }),
  });
}

/** D-133 A-W2 — auto-proposed supplier for a complaint's brand (read-only). */
export function useSupplierSuggestion(complaintId: string | null, enabled = true) {
  return useQuery({
    queryKey: ["complaint", complaintId, "supplierSuggestion"],
    queryFn: ({ signal }) => fetchSupplierSuggestion(complaintId!, signal),
    enabled: enabled && !!complaintId,
    staleTime: 30_000,
  });
}

/** D-133 C-W3 — prepare the supplier-email packet (recipient suggestion + safe body
 *  + attachment selector + caps). Loaded lazily when the send modal opens. */
export function useSupplierPacket(complaintId: string | null, enabled = true) {
  return useQuery({
    queryKey: ["complaint", complaintId, "supplierPacket"],
    queryFn: ({ signal }) => fetchSupplierPacket(complaintId!, signal),
    enabled: enabled && !!complaintId,
    staleTime: 15_000,
  });
}

/** D-133 D-W1/D-W2 — on-demand AI-переклад тіла (uk→target) через gateway. Ефемерний
 *  (draft у модалі, не auto-send) → БЕЗ invalidation. Gateway-fail повертається як
 *  status:'unavailable' (не throw) → модал дає ручне редагування (D-W1a). */
export function useTranslateSupplierBody(complaintId: string | null) {
  return useMutation({
    mutationFn: (targetLang: string) => translateSupplierBody(complaintId!, { targetLang }),
  });
}

/** D-133 C-W3 — send the supplier email (2-step; button disabled while pending). */
export function useSendSupplierEmail(complaintId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SupplierSendPayload) => sendSupplierEmail(complaintId!, body),
    onSuccess: () => {
      if (complaintId) {
        qc.invalidateQueries({ queryKey: ["complaints", "detail", complaintId] });
        qc.invalidateQueries({ queryKey: ["complaint", complaintId, "supplierPacket"] });
      }
      qc.invalidateQueries({ queryKey: ["complaints", "list"] });
    },
  });
}

/** D-133 E-W2 — record a manual supplier reply → timeline + supplierStatus=replied +
 *  notify author/responsible. Refresh the detail (stage/card/timeline) + list badge. */
export function useAddSupplierResponse(complaintId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SupplierResponsePayload) => addSupplierResponse(complaintId!, body),
    onSuccess: () => {
      if (complaintId) {
        qc.invalidateQueries({ queryKey: ["complaints", "detail", complaintId] });
      }
      qc.invalidateQueries({ queryKey: ["complaints", "list"] });
    },
  });
}
